// Cloud Function API helpers for the quote admin panel + reservation/arrival forms.
// All three CFs are HTTP (gen2, us-central1), public, and accept/return JSON.
//
// SAME-ORIGIN PROXY (session 34): the widget calls them through the Firebase
// Hosting rewrite `/api/<fn>` (infra/firebase/firebase.json) instead of the
// direct *.cloudfunctions.net URL. A corporate firewall that happily serves
// onebeachbot.web.app may block cloudfunctions.net as an unknown category and
// synthesize its own 404 (Stefanie's office network did exactly that — the
// page loaded, the data "404'd", the request never reached Google). Through
// the hosting proxy the data rides the origin that already loaded the page.
// The direct endpoints stay live (backward compat, ops scripts).

const BASE = "/api";

/**
 * Load a booking by its opaque token. Used by both the admin panel and
 * the reservation form to get booking data from the server (not from the token).
 *
 * Contract (session 34): `null` means the TOKEN is bad (401/404 — caller shows
 * the "link expired / ask for a fresh one" page). Any transient failure —
 * network down, CF error, unreachable — THROWS, so the caller can show a
 * retry-able error state instead of mistaking it for a dead link (the old
 * null-on-anything contract rendered a form full of empty locked fields —
 * Stefanie's "it will not let me").
 *
 * @param {string} token - the opaque HMAC token from the URL.
 * @returns {Promise<Object|null>} the booking record, or null on 401/404.
 */
export async function loadBookingByToken(token) {
  if (!token) return null;
  const resp = await fetch(`${BASE}/loadBookingByToken`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (resp.status === 401 || resp.status === 404) return null;
  if (!resp.ok) throw new Error(`loadBookingByToken failed: HTTP ${resp.status}`);
  return await resp.json();
}

/**
 * Scrape fresh per-day room rates for a date range. Used by the admin panel's
 * "Scrape" button.
 * @param {string} checkin - ISO date "YYYY-MM-DD".
 * @param {string} checkout - ISO date "YYYY-MM-DD".
 * @returns {Promise<Array<{date:string,rate:number,source:string}>>}
 */
export async function scrapeRoomRate(checkin, checkout) {
  if (!checkin || !checkout) return [];
  try {
    const resp = await fetch(`${BASE}/scrapeRoomRate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ checkin, checkout }),
    });
    if (!resp.ok) return [];
    return await resp.json();
  } catch {
    return [];
  }
}

/**
 * Approve + send a quote. Writes money columns to the sheet, sets quote_sent=yes.
 * The brain's IntakeReactor detects the flip and sends the email within 1 min.
 * @param {Object} params - { booking_id, token, nightly_rates, cleaning_fee, wedding, discount_subject, discount_percentage }
 * @returns {Promise<{ok:boolean, quote?:Object, sent?:string}|null>}
 */
export async function quoteApproveSend(params) {
  if (!params || !params.token || !params.booking_id) return null;
  try {
    const resp = await fetch(`${BASE}/quoteApproveSend`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
    });
    if (resp.status === 401) return { ok: false, error: "invalid_token" };
    if (resp.status === 403) return { ok: false, error: "vrbo_blocked" };
    if (!resp.ok) return { ok: false, error: "server_error" };
    return await resp.json();
  } catch {
    return { ok: false, error: "network_error" };
  }
}
